import { useEffect, useState, useCallback } from 'react'
import { apiAgentGetProfile, ApiRequestError, type AdminPartner } from '@/lib/api'
import { format } from 'date-fns'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { AlertCircle, RefreshCw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useT } from '@/contexts/language'
import { ChangePasswordForm } from '@/components/ChangePasswordForm'

function Field({ label, value, mono = true }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-start gap-4 py-3 border-b border-border last:border-0">
      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground w-36 shrink-0 pt-0.5">
        {label}
      </span>
      <span className={cn('text-sm text-foreground break-all', mono && 'font-mono')}>
        {value}
      </span>
    </div>
  )
}

export default function AgentProfilePage() {
  const t = useT()
  const [profile, setProfile] = useState<AdminPartner | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setProfile(await apiAgentGetProfile())
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.common.error)
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => { void load() }, [load])

  return (
    <div className="p-4 md:p-6 max-w-lg">
      <div className="flex items-center justify-between mb-5">
        <span className="font-mono text-xs font-bold uppercase tracking-wider text-foreground">
          {t.nav.profile}
        </span>
        <Button variant="ghost" size="sm" onClick={() => void load()} disabled={loading} className="h-7 gap-1.5 font-mono text-xs text-muted-foreground">
          <RefreshCw className={cn('h-3 w-3', loading && 'animate-spin')} />
          {t.common.refresh}
        </Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 border border-destructive/40 rounded-[var(--radius)] bg-card mb-4" role="alert">
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
          <p className="font-mono text-xs text-destructive">{error}</p>
        </div>
      )}

      <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden shadow-[var(--shadow-sm)]">
        <div className="px-4 py-2.5 bg-secondary flex items-center justify-between">
          <span className="font-mono text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
            Account Info
          </span>
          {profile && (
            <Badge variant={profile.isActive ? 'success' : 'destructive'} className="font-mono text-[10px]">
              {profile.isActive ? t.common.active : t.common.inactive}
            </Badge>
          )}
        </div>

        <div className="px-4">
          {loading ? (
            <div className="flex flex-col gap-4 py-4">
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className="flex gap-4">
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-3 w-48" />
                </div>
              ))}
            </div>
          ) : profile ? (
            <>
              <Field label="Name"        value={profile.name} />
              <Field label="Code"        value={profile.code} />
              <Field label="Agent Share" value={`${(parseFloat(profile.commissionRate) * 100).toFixed(0)}% (Admin: ${(100 - parseFloat(profile.commissionRate) * 100).toFixed(0)}%)`} />
              <Field label="Wallet Type" value={profile.walletType} />
              {profile.walletUrl && <Field label="Wallet URL" value={profile.walletUrl} />}
              <Field label="Status"      value={profile.status} />
              <Field label="Member Since" value={format(new Date(profile.createdAt), 'dd MMM yyyy')} mono={false} />
            </>
          ) : null}
        </div>
      </div>

      <ChangePasswordForm />
    </div>
  )
}
